import React, { useState, useContext } from 'react';
import { LoadingSpinner, DataTable, columnTypes, dateTranslate, Dropdown, Pagination } from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import VideoPlaylistButton from './VideoPlaylistButton';
import { store } from '../store';
import { mapPlaylistFromApiPlaylist } from '../utils/playlists';

const getColumns = (translate, roles) => [
    {
        id: 'playlist',
        title: translate('components.playlist-report.playlist'),
        type: columnTypes.NODE,
        transform: (row) => (
            <div className="d-flex align-items-center">
                <VideoPlaylistButton playlist={mapPlaylistFromApiPlaylist(row, translate, roles)} hideFilter />
                <div className="ms-2">{row.Name}</div>
            </div>
        ),
        headerClass: 'w-25',
    },
    {
        title: translate('components.playlist-report.date-added'),
        id: 'dateAdded',
        type: columnTypes.NODE,
        transform: (row) => dateTranslate(translate, row.PlaylistDate ?? row.LastUpdateDate),
        headerClass: 'w-25',
    },
    {
        title: translate('components.playlist-report.expires'),
        id: 'expirationDate',
        type: columnTypes.NODE,
        transform: (row) => (row.Expires ? dateTranslate(translate, row.Expires) : '--'),
        headerClass: 'w-25',
    },
    {
        title: translate('components.playlist-report.comments'),
        id: 'comments',
        type: columnTypes.NODE,
        transform: (row) => <span dangerouslySetInnerHTML={{ __html: row.Comments.filter((c) => c).join('<br />') || '--' }} />,
        headerClass: 'w-25',
    },
];

const PaginatedPlaylistDataTable = ({ rows, columns }) => {
    const resultsPerPage = 10;
    const [page, setPage] = useState(1);
    const [displayedRows, setDisplayedRows] = useState(rows.slice(0, resultsPerPage));
    const changePage = (newPage) => {
        setPage(newPage);
        const firstIndex = (newPage - 1) * resultsPerPage;
        setDisplayedRows(rows.slice(firstIndex, firstIndex + resultsPerPage));
    };
    return (
        <>
            <DataTable
                rows={displayedRows}
                columns={columns}
            />
            {rows.length > resultsPerPage && (
                <Pagination position="left" totalItems={rows.length} page={page} itemsPerPage={resultsPerPage} onPageChange={changePage} />
            )}
        </>
    );
};

const PlaylistReport = ({ title, isLoading, playlists }) => {
    const { t: translate } = useTranslation();
    const { state: { user: loggedInUser } } = useContext(store);
    const [selectedCategory, setSelectedCategory] = useState({ name: translate('common.all'), value: null });

    const roles = loggedInUser.Roles.map((r) => r.RoleName);

    if (isLoading) {
        return <div className="d-flex justify-content-center py-5"><LoadingSpinner /></div>;
    }

    const categoryPlaylists = Object.values(playlists.reduce((allPlaylists, playlist) => {
        (allPlaylists[playlist.CategoryID] = allPlaylists[playlist.CategoryID] || []).push(playlist);
        return allPlaylists;
    }, {}))
        .sort((a, b) => {
            const one = a[0]?.PlayListCategory?.Name ?? null;
            const two = b[0]?.PlayListCategory?.Name ?? null;

            if (one === null) return 1;
            if (two === null) return -1;

            return one.toLowerCase().localeCompare(two.toLowerCase());
        });

    const categoryOptions = [
        { name: translate('common.all'), value: null },
        ...categoryPlaylists.map((playlist) => ({
            name: playlist[0]?.PlayListCategory?.Name ?? '--',
            value: playlist[0]?.CategoryID ?? null,
        }))];

    return (
        <div className="p-2">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">{ title }</h3>
                <Dropdown
                    options={categoryOptions}
                    value={selectedCategory}
                    onSelect={(value) => setSelectedCategory(value)}
                    align="end"
                />
            </div>
            {playlists.length
                ? categoryPlaylists
                    .filter((category) => !selectedCategory.value || +category[0].CategoryID === selectedCategory.value)
                    .map((categoryPlaylist) => {
                        const category = categoryPlaylist[0].PlayListCategory;
                        categoryPlaylist.sort((a, b) => {
                            const one = new Date(a.PlaylistDate ?? a.LastUpdateDate);
                            const two = new Date(b.PlaylistDate ?? b.LastUpdateDate);

                            return two < one ? -1 : 1;
                        });

                        return (
                            <div key={categoryPlaylist[0].CategoryID} className="bg-white rounded border py-3 px-4 mb-3">
                                <h4>{ category?.Name ?? '--' }</h4>
                                <PaginatedPlaylistDataTable columns={getColumns(translate, roles)} rows={categoryPlaylist} />
                            </div>
                        );
                    })
                : <div className="d-flex justify-content-center py-5">{translate('components.playlist-report.no-playlist')}</div>}
        </div>
    );
};

export default PlaylistReport;
